import { useEffect, useRef, useState } from "react"

import { ExportButton } from "@/molecules/ExportButton"
import { Filter } from "@/organisms/FilterNew/Filter"
import { KodemScoreFilter } from "@/organisms/FilterNew/KodemScoreFilter"
import { InputSmartSearchLoading, SmartSearch } from "@/organisms/SmartSearch"
import { useFiltersTriage } from "@/pages/TriagePage/application/useTriage"
import * as filterActions from "./getFilters"

import type { IssuesFilterComponent } from "./IssuesFilter.types"

export const IssuesFilter: IssuesFilterComponent = (props) => {
  const initialState = useRef(filterActions.getInitialValues(props)).current
  const [filters, setFilters] = useState({
    issueTypeFilterIndex: -1,
    loading: true,
    values: filterActions.getFilters(props)
  })
  const {
    t,
    searchedVulnerabilities,
    params,
    exportQueryParams,
    isDataAvailable,
    placement,
    defaultParams
  } = props
  const context = useFiltersTriage()
  const isFilterLoading = context[1]
  const disableExport = !isDataAvailable || isFilterLoading

  const exportParams = { ...defaultParams, ...params }

  useEffect(() => {
    if (!isFilterLoading) {
      const owaspFilter = context[0].response?.allFilters.find(
        ({ key }) => key === "owasp"
      )
      const labelFilter = context[0].response?.allFilters.find(
        ({ key }) => key === "label"
      )
      const issueTypeFilter = context[0].response?.allFilters.find(
        ({ key }) => key === "issueType"
      )
      const remediationEffortFilter = context[0].response?.allFilters.find(
        ({ key }) => key === "remediationEffort"
      )

      const values = filters.values.map((filter) => {
        if (filter.key === "owasp" && owaspFilter) return owaspFilter
        if (filter.key === "issueType" && issueTypeFilter) {
          const { options, ...rest } = issueTypeFilter

          return {
            options: options?.filter(({ value }) =>
              props.defaultParams?.issueType?.includes(value)
            ),
            ...rest
          }
        }
        if (filter.key === "remediationEffort" && remediationEffortFilter)
          return remediationEffortFilter

        if (filter.key !== "label" && filter.key !== "notLabel") return filter

        return {
          ...filter,
          options: labelFilter?.options
        }
      })

      setFilters({ ...filters, loading: false, values })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterLoading])

  useEffect(() => {
    setFilters(({ issueTypeFilterIndex, ...prevState }) => ({
      issueTypeFilterIndex: prevState.values.findIndex(
        ({ key }) => key === "issueType"
      ),
      ...prevState
    }))
  }, [filters.values])

  const { issueTypeFilterIndex } = filters
  const itemsIssueTypes = filters.values[issueTypeFilterIndex]?.options || []

  return (
    <Filter.Provider
      initialState={initialState}
      useQueryString={false}
      onChange={props.onChange}
      isLoading={filters.loading}
    >
      <Filter.Template
        isLoading={filters.loading}
        aside={
          <ExportButton
            isLoading={disableExport}
            exportType="IssuesDrawers"
            filters={exportQueryParams?.(exportParams)}
          />
        }
        search={
          filters.loading ? (
            <InputSmartSearchLoading />
          ) : (
            <SmartSearch
              id={props.placement || "FilterIssuesTabDrawer"}
              filters={filters.values}
              searchedVulnerabilities={searchedVulnerabilities}
            />
          )
        }
      >
        <Filter.Checkbox
          key={issueTypeFilterIndex}
          keyFilter="issueType"
          items={itemsIssueTypes}
          placeHolderDefaultValue="Issue Type"
          isLoading={filters.loading}
          placement={placement}
        />
        <Filter.FixInsights
          filters={filters.values}
          isLoading={isFilterLoading}
          placement={placement}
        />
        <Filter.Checkbox
          order="none"
          keyFilter="severity"
          items={filterActions.SEVERITIES_FILTER_OPTIONS}
          placeHolderDefaultValue="Severity"
          isLoading={filters.loading}
          placement={placement}
        />
        <Filter.Insights
          filters={filters.values}
          isLoading={isFilterLoading}
          placement={placement}
        />
        <KodemScoreFilter
          maxValue={1000}
          placeHolderDefaultValue="Score"
          label={t("general.kodemScore.plain")}
          prefix={`${t("general.kodemScore.plain")} ≥`}
          isLoading={filters.loading}
          placement={placement}
        />
      </Filter.Template>
    </Filter.Provider>
  )
}
